/**
 * Parallel Qobuz + local library search (v1 — no deduplication).
 * Fires both queries concurrently; normalizes local tracks to a shared catalog shape.
 */

import { libraryService } from './libraryService';
import {
  ensureLibraryRoutingReady,
  shouldUseLocalLibraryIpc,
} from './localLibraryIpcGate';
import type { TrackSummary } from './localLibrary/LocalLibraryAdapter';
import {
  searchService,
  type SearchTrack,
  type UnifiedSearchResult,
} from './searchService';

export type CatalogSource = 'qobuz' | 'local';

/** Shared track shape for cross-catalog search results (v1). */
export interface CatalogSearchTrack {
  id: string;
  title: string;
  artist: string;
  album: string;
  duration: number;
  source: CatalogSource;
  artistName?: string;
  albumTitle?: string;
  coverUrl?: string | null;
  artworkUrl?: string | null;
  sourceLabel?: string;
  isPreview?: boolean;
  maxBitDepth?: number | null;
  maxSampleRate?: number | null;
  file_path?: string;
  machine_id?: string;
  format?: string;
  serviceTrackId?: string;
}

export interface ParallelCatalogSearchResult {
  qobuz: UnifiedSearchResult | null;
  qobuzError: string | null;
  localTracks: CatalogSearchTrack[];
}

export const QOBUZ_SEARCH_ERROR_MESSAGE =
  'Qobuz search failed. Check your connection or sign in to Qobuz.';

let _localSourcesConfigured: boolean | null = null;

export function invalidateLocalLibrarySourcesCache(): void {
  _localSourcesConfigured = null;
}

/** True when local-core IPC is available and at least one library source exists. */
export async function hasLocalLibrarySourcesConfigured(force = false): Promise<boolean> {
  if (!shouldUseLocalLibraryIpc()) {
    _localSourcesConfigured = false;
    return false;
  }
  if (!force && _localSourcesConfigured !== null) {
    return _localSourcesConfigured;
  }
  try {
    await ensureLibraryRoutingReady();
    const resp = await libraryService.getSources();
    const sources = Array.isArray(resp?.sources) ? resp.sources : [];
    _localSourcesConfigured = sources.length > 0;
  } catch {
    _localSourcesConfigured = false;
  }
  return _localSourcesConfigured;
}

export function normalizeQobuzSearchTrack(track: SearchTrack): CatalogSearchTrack {
  return {
    id: track.id,
    title: track.title,
    artist: track.artistName,
    album: track.albumTitle,
    duration: track.duration || 0,
    source: 'qobuz',
    artistName: track.artistName,
    albumTitle: track.albumTitle,
    coverUrl: track.coverUrl,
    sourceLabel: track.sourceLabel || 'Qobuz',
    isPreview: track.isPreview,
    maxBitDepth: track.maxBitDepth,
    maxSampleRate: track.maxSampleRate,
    serviceTrackId: track.serviceTrackId,
  };
}

export function normalizeLocalSearchTrack(track: TrackSummary): CatalogSearchTrack {
  const artist = String(track.artist || track.artist_name || '');
  const album = String(track.album || track.album_title || '');
  return {
    id: track.id,
    title: track.title || 'Unknown',
    artist,
    album,
    duration: track.duration || 0,
    source: 'local',
    sourceLabel: 'Library',
    file_path: track.file_path ?? undefined,
    machine_id: track.machine_id ?? undefined,
    format: track.format ?? undefined,
  };
}

function normalizeLocalSearchTracks(tracks: TrackSummary[], cap: number): CatalogSearchTrack[] {
  return tracks.slice(0, cap).map(normalizeLocalSearchTrack);
}

async function fetchLocalSearchTracks(query: string, limit: number): Promise<CatalogSearchTrack[]> {
  try {
    const resp = await libraryService.searchLocal(query, limit);
    const tracks = Array.isArray(resp?.tracks) ? resp.tracks : [];
    return normalizeLocalSearchTracks(tracks, limit);
  } catch {
    return [];
  }
}

/**
 * Run Qobuz unified search and local library search in parallel.
 * Local leg is skipped when no library sources are configured.
 */
export async function runParallelCatalogSearch(
  query: string,
  qobuzLimit = 15,
  localLimit = 10,
): Promise<ParallelCatalogSearchResult> {
  const trimmed = (query || '').trim();
  const hasLocal = await hasLocalLibrarySourcesConfigured();

  const qobuzPromise = searchService
    .unified(trimmed, qobuzLimit)
    .then((data) => ({ data, error: null as string | null }))
    .catch((err: unknown) => ({
      data: null,
      error: err instanceof Error ? err.message : 'Search failed. Please try again.',
    }));

  const localPromise: Promise<CatalogSearchTrack[]> = hasLocal
    ? fetchLocalSearchTracks(trimmed, localLimit)
    : Promise.resolve([]);

  const [qobuzOutcome, localTracks] = await Promise.all([qobuzPromise, localPromise]);

  return {
    qobuz: qobuzOutcome.data,
    qobuzError: qobuzOutcome.error,
    localTracks,
  };
}

/** Run local search; never throws (IPC/daemon failures → []). */
export async function searchLocalCatalogTracks(
  query: string,
  limit: number,
): Promise<CatalogSearchTrack[]> {
  return fetchLocalSearchTracks(query, limit);
}

/** Gate + search; never throws. */
export function fetchLocalSearchIfConfigured(
  query: string,
  limit: number,
): Promise<CatalogSearchTrack[]> {
  return hasLocalLibrarySourcesConfigured()
    .then((hasLocal) => (hasLocal ? fetchLocalSearchTracks(query, limit) : []))
    .catch(() => []);
}
